<template>
  <div class="attribute-group-rule" @click="modals.open = true">
    <div class="path-presenter-wrapper">
      <PathPresenter v-model="vm.selectedPath" />
    </div>
    <AttributeGroupSelectionModal
      v-model="modals.open"
      @confirm="onConfirm"
      @hide="onHideHandler"
      :selected-entry-id="localModel"
      :selected-path="selectedPath" />
  </div>
</template>

<script setup lang="ts">
import AttributeGroupSelectionModal from "@common/components/MultiLevelNavigation/AttributeGroupSelectionModal.vue";
import PathPresenter from "@common/components/PathPresenter.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { BasicSelectionModel } from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { AttributeGroup } from "@medapro-types/common/common";
import { Branch, MultiLevelNavigationConfirmModel } from "@medapro-types/common/components/MultiLevelNavigation";
import { PathPresentedDataModel } from "@medapro-types/common/components/PathPresenter";
import attributeGroupsApi from "@services/attributeGroups.js";
import _ from "lodash";
import { Ref, computed, inject, nextTick, onMounted, reactive } from "vue";

//#region General
const props = defineProps<{ value: string }>();
const emits = defineEmits(["input", "deleteRule"]);
//#endregion

//#region Store
const { primaryLanguageCode } = useContentLanguages();

//#endregion

//#region Inject
const isBusy = inject<Ref<boolean>>("advancedSearchIsBusy");
//#endregion

//#region Viewmodel
type ViewModel = { selectedPath: BasicSelectionModel[] };
const vm = reactive<ViewModel>({ selectedPath: [] });

const modals = reactive({ open: false });
//#endregion

//#region Busy
const setBusyState = (busyValue: boolean) => {
  if (isBusy) {
    isBusy.value = busyValue;
  }
};
//#endregion

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv) {
    emits("input", nv);
  }
});

//#region SelectedPath
const selectedPath = computed<Branch[]>(() =>
  vm.selectedPath?.filter((x) => x.id != localModel.value).map((x, index) => ({ id: x.id, depth: index, name: x.name, parentId: null }))
);
//#endregion

//#region EventHandler
const onHideHandler = () => {
  nextTick(() => {
    if (_.isEmpty(localModel.value)) emits("deleteRule");
  });
};

const onConfirm = async (data: MultiLevelNavigationConfirmModel) => {
  if (data.selection && data.selection.id) {
    localModel.value = data.selection.id;
    await fetchAndSetPath(data.selection.id);
  }
};

const fetchAndSetPath = async (id: string) => {
  setBusyState(true);
  const fetchedPath: AttributeGroup[] = await attributeGroupsApi.getPath(primaryLanguageCode.value, id);
  const mappedFetchedPath: PathPresentedDataModel[] = fetchedPath.map((x) => ({ id: x.attributeGroupId, name: x.name }));
  const root = _.first(mappedFetchedPath);
  if (root) root.icon = "Home";
  vm.selectedPath = mappedFetchedPath;
  setBusyState(false);
};
//#endregion

//#region LifeCycle
onMounted(async () => {
  if (!props.value) {
    modals.open = true;
  } else {
    await fetchAndSetPath(props.value);
  }
});
//#endregion
</script>
